import { useState, ChangeEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { RoleDto, useListRolesQuery } from "@/store/apiSlice";
import { PlayerService } from "@/lib/services/player";
import { toast } from "sonner";

type BulkSummary = {
  total: number;
  success: number;
  failed: { rowNumber: number; username: string; email: string; error: string }[];
} | null;

interface BulkCreateUsersContentProps {
  roles?: RoleDto[];
  summary: BulkSummary;
  setSummary: (s: BulkSummary) => void;
}

export function BulkCreateUsersContent({
  roles,
  summary,
  setSummary,
}: BulkCreateUsersContentProps) {
  const [file, setFile] = useState<File | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const hasRoles = roles && roles.length > 0;

  const handleTemplateDownload = () => {
    const header =
      "username,email,first_name,last_name,phone_number,team,is_active,is_verified,password\n";
    const example =
      "john.doe,john@example.com,John,Doe,+910000000000,RED_TEAM,true,true,TempPass123!\n";
    const blob = new Blob([header + example], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "bulk_users_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] || null;
    setFile(f);
    setSummary(null);
  };

  const parseBoolean = (value: string | undefined): boolean => {
    if (!value) return true;
    const v = value.toLowerCase().trim();
    return v === "true" || v === "1" || v === "yes";
  };

  const handleProcess = async () => {
    if (!file) {
      toast.error("Please select a CSV file.");
      return;
    }
    setIsProcessing(true);
    setSummary(null);

    try {
      const text = await file.text();
      const lines = text
        .split(/\r?\n/)
        .map((l) => l.trim())
        .filter((l) => l.length > 0);
      if (lines.length <= 1) {
        toast.error("CSV file appears to be empty.");
        return;
      }

      const header = lines[0].split(",");
      const getIndex = (name: string) =>
        header.findIndex((h) => h.toLowerCase().trim() === name);

      const idxUsername = getIndex("username");
      const idxEmail = getIndex("email");
      const idxFirstName = getIndex("first_name");
      const idxLastName = getIndex("last_name");
      const idxPhone = getIndex("phone_number");
      const idxTeam = getIndex("team");
      const idxIsActive = getIndex("is_active");
      const idxIsVerified = getIndex("is_verified");
      const idxPassword = getIndex("password");

      if (
        idxUsername === -1 ||
        idxEmail === -1 ||
        idxTeam === -1 ||
        idxPassword === -1
      ) {
        toast.error(
          "Template mismatch. Required columns: username, email, team, password (plus optional others)."
        );
        return;
      }

      const failed: { rowNumber: number; username: string; email: string; error: string }[] =
        [];
      let success = 0;

      for (let i = 1; i < lines.length; i++) {
        const row = lines[i];
        if (!row) continue;
        const cols = row.split(",");
        const username = cols[idxUsername]?.trim() ?? "";
        const email = cols[idxEmail]?.trim() ?? "";
        const team = cols[idxTeam]?.trim() ?? "";
        const first_name = idxFirstName >= 0 ? cols[idxFirstName]?.trim() ?? "" : "";
        const last_name = idxLastName >= 0 ? cols[idxLastName]?.trim() ?? "" : "";
        const phone_number = idxPhone >= 0 ? cols[idxPhone]?.trim() ?? "" : "";
        const is_active = parseBoolean(
          idxIsActive >= 0 ? cols[idxIsActive]?.trim() : undefined
        );
        const is_verified = parseBoolean(
          idxIsVerified >= 0 ? cols[idxIsVerified]?.trim() : undefined
        );
        const password = cols[idxPassword]?.trim() ?? "";

        if (!username || !email || !team || !password) {
          failed.push({
            rowNumber: i + 1,
            username,
            email,
            error: "username, email, team, and password are required.",
          });
          continue;
        }

        // Map team label/code to team_id (GET /api/teams/)
        let team_id: number | undefined = undefined;
        if (roles && roles.length > 0) {
          const t = team.toLowerCase();
          const match = roles.find((r) => {
            const name = (r.name || "").toLowerCase();
            const code = (r.code || "").toLowerCase();
            return name === t || code === t;
          });
          if (match) {
            team_id = match.id;
          } else {
            failed.push({
              rowNumber: i + 1,
              username,
              email,
              error: `Unknown team: ${team}`,
            });
            continue;
          }
        }

        try {
          await PlayerService.create({
            username,
            email,
            first_name: first_name || undefined,
            last_name: last_name || undefined,
            phone_number: phone_number || undefined,
            password,
            password_confirm: password,
            team_id,
            is_active,
            is_verified,
          });
          success += 1;
        } catch (e: any) {
          const data = e?.response?.data;
          let message = "Failed to create user.";
          if (data && typeof data === "object") {
            const parts: string[] = [];
            Object.entries(data).forEach(([field, value]) => {
              if (Array.isArray(value)) {
                (value as any[]).forEach((msg) =>
                  parts.push(`${field}: ${String(msg)}`)
                );
              } else if (value) {
                parts.push(`${field}: ${String(value)}`);
              }
            });
            if (parts.length) {
              message = parts.join("; ");
            }
          }
          failed.push({
            rowNumber: i + 1,
            username,
            email,
            error: message,
          });
        }
      }

      const total = lines.length - 1;
      setSummary({ total, success, failed });
      toast.success(
        `Bulk upload complete. Success: ${success}, Failed: ${failed.length}.`
      );
    } catch (err) {
      console.error(err);
      toast.error("Failed to process file.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Step 1: Download template */}
      <div className="space-y-2 rounded-md border border-dashed border-border bg-muted/40 p-3">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Step 1 · Download template
        </p>
        <p className="text-xs text-muted-foreground">
          Use this CSV template as a starting point. Columns:
          <span className="block mt-1">
            <code className="font-mono text-[11px]">
              username,email,first_name,last_name,phone_number,team,is_active,is_verified,password
            </code>
          </span>
        </p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-1"
          onClick={handleTemplateDownload}
        >
          Download template
        </Button>
      </div>

      {/* Step 2: Upload file */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          Step 2 · Upload file
        </p>
        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
          <Input type="file" accept=".csv" onChange={handleFileChange} className="sm:flex-1" />
          <Button
            type="button"
            size="sm"
            onClick={handleProcess}
            disabled={isProcessing || !file}
          >
            {isProcessing ? "Processing..." : "Process upload"}
          </Button>
        </div>
        {!hasRoles && (
          <p className="text-xs text-amber-600">
            Note: No teams loaded. Team validation will be limited.
          </p>
        )}
        {file && (
          <p className="text-xs text-muted-foreground">
            Selected file: <span className="font-medium">{file.name}</span>
          </p>
        )}
      </div>

      {/* Step 3: Results */}
      {summary && (
        <div className="mt-2 space-y-2 border-t border-border pt-3">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Step 3 · Results
          </p>
          <p className="text-xs text-muted-foreground">
            Total records: <span className="font-semibold">{summary.total}</span> · Success:{" "}
            <span className="font-semibold text-emerald-600">{summary.success}</span> · Failed:{" "}
            <span className="font-semibold text-red-600">{summary.failed.length}</span>
          </p>
          {summary.failed.length > 0 && (
            <div className="mt-2 max-h-48 overflow-auto border border-border rounded-md bg-card">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-1 text-left w-12">Row</th>
                    <th className="px-2 py-1 text-left">Username</th>
                    <th className="px-2 py-1 text-left">Email</th>
                    <th className="px-2 py-1 text-left">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.failed.map((f) => (
                    <tr key={`${f.rowNumber}-${f.email}`}>
                      <td className="px-2 py-1 align-top">{f.rowNumber}</td>
                      <td className="px-2 py-1 align-top break-all">{f.username}</td>
                      <td className="px-2 py-1 align-top break-all">{f.email}</td>
                      <td className="px-2 py-1 align-top">{f.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

